import React, { useState, useEffect } from 'react';
import SearchResult from './components/search-result';
import SearchExposedFilters from './components/search-exposed-filters';
import SearchResultCount from './components/search-result-count';
import SearchPagination from './components/search-pagination';
import SearchAd from './components/search-ad';

export default function App() {
  const [filters, setFilters] = useState({
    keyword: '',
    type: '',
    subtype: '',
    subtype_sub_level: '',
    topic: '',
    subtopic: '',
    desc: true,
    page: 1,
    page_size: 12,
    year: '',
    conference: '',
    sortmode: 'Date',
    matching: 'AND',
    searched: true,
  });
  const [sortByDateClass, setSortByDateClass] = useState(true);
  const [searchResults, setSearchResults] = useState(null);
  const [newsSubtypes, setNewsSubtypes] = useState(null);
  const [newsConferences, setNewsConferences] = useState(null);
  const [podcastSubtypes, setPodcastSubtypes] = useState(null);
  const [specialties, setSpecialties] = useState(null);
  const [opinionSubtypeSubLevel, setOpinionSubtypeSubLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  // eslint-disable-next-line no-undef
  const apiSearchURL = new URL(`${window.location.origin}/api/v1/search`);
  const apiContentTypesURL = new URL(
    `${window.location.origin}/api/v1/content/subtypes`,
  );
  const apiConferenceTagsURL = new URL(
    `${window.location.origin}/api/v1/conference/tags`,
  );
  const apiSpecialtiesURL = new URL(
    `${window.location.origin}/api/v1/content/specialties`,
  );
  const apiSubtypeSubLevelURL = new URL(
    `${window.location.origin}/api/v1/content/subtypes_sub_level`,
  );

  // Search function.
  function search(searchFilters) {
    setSearchResults(null);
    setLoading(true);
    setWarning(false);

    // Do the search.
    apiSearchURL.search = new URLSearchParams(searchFilters).toString();

    fetch(apiSearchURL)
      .then((response) => response.json())
      // eslint-disable-next-line no-shadow
      .then(({ data }) => {
        setSearchResults(data);
      })
      // eslint-disable-next-line no-shadow
      .catch((error) => {
        setError(error);
        console.error('Error fetching data: ', error);
      })
      .finally(() => {
        setLoading(false);
      });

    // Update the page URL.
    const newURL = `${window.location.protocol}//${window.location.host}${window.location.pathname}${apiSearchURL.search}`;
    window.history.pushState({ path: newURL }, '', newURL);
  }

  function updateSortModeClass(sortMode) {
    const isDateSortMode = sortMode === 'Date';
    setSortByDateClass(isDateSortMode);
  }

  function handleSortContent(event) {
    event.preventDefault();
    const sortMode = event.target.value;
    const updatedFilters = {
      ...filters,
      sortmode: sortMode,
      page: 1,
    };
    setFilters(updatedFilters);
    search(updatedFilters);
    updateSortModeClass(sortMode);
  }

  function handlePageChange(page) {
    const updatedFilters = {
      ...filters,
      page,
    };
    setFilters(updatedFilters);
    search(updatedFilters);
  }

  useEffect(() => {
    function transformConferenceTags(data) {
      const withoutYear = data.map(({ tag }) => tag.replace(/\B(?=[A-Z])|\s*\d{4}\b/g, ''));
      const withoutDuplicates = [...new Set(withoutYear)];
      const objectArray = withoutDuplicates.map((item) => ({ tag: item }));
      return [{ tag: 'any' }, ...objectArray];
    }

    function getConferences() {
      fetch(apiConferenceTagsURL)
        .then((response) => response.json())
        // eslint-disable-next-line no-shadow
        .then(({ data }) => {
          const updatedData = transformConferenceTags(data);
          setNewsConferences(updatedData);
        })
        // eslint-disable-next-line no-shadow
        .catch((error) => {
          setError(error);
          console.error('Error fetching data: ', error);
        });
    }

    function getSpecialties() {
      fetch(apiSpecialtiesURL)
        .then((response) => response.json())
        // eslint-disable-next-line no-shadow
        .then(({ data }) => {
          setSpecialties(data);
        })
        // eslint-disable-next-line no-shadow
        .catch((error) => {
          setError(error);
          console.error('Error fetching data: ', error);
        });
    }

    function getSubTypes(subtype, callback) {
      apiContentTypesURL.search = new URLSearchParams({
        type: subtype,
      }).toString();

      fetch(apiContentTypesURL)
        .then((response) => response.json())
        // eslint-disable-next-line no-shadow
        .then(({ data }) => {
          callback(data);
        })
        // eslint-disable-next-line no-shadow
        .catch((error) => {
          setError(error);
        });
    }

    function getSubTypeSubLevel(subtype, callback) {
      apiSubtypeSubLevelURL.search = new URLSearchParams({
        subtype,
      }).toString();

      fetch(apiSubtypeSubLevelURL)
        .then((response) => response.json())
        // eslint-disable-next-line no-shadow
        .then(({ data }) => {
          callback(data);
        })
        // eslint-disable-next-line no-shadow
        .catch((error) => {
          setError(error);
        });
    }

    // Get filters data from API.
    getConferences();
    getSpecialties();
    getSubTypes('news', setNewsSubtypes);
    getSubTypes('audio', setPodcastSubtypes);
    getSubTypeSubLevel('Opinion', setOpinionSubtypeSubLevel);

    // Get filters from the URL.
    const searchParams = new URLSearchParams(window.location.search);
    const overridedFilters = {};
    searchParams.forEach((value, key) => {
      overridedFilters[key] = value;
    });

    const updatedFilters = {
      ...filters,
      ...overridedFilters,
    };

    setFilters(updatedFilters);
    updateSortModeClass(updatedFilters.sortmode);
    search(updatedFilters);

    return function cleanup() {
      setLoading(true);
      setSearchResults(null);
    };
  }, []);

  const renderedTitle = loading ? (
    <h1 className="search-page__title">Loading...</h1>
  ) : (
    <h1 className="search-page__title">Search Results</h1>
  );
  const renderedResultItems = searchResults
    ? searchResults.items.map((item) => (
      <li key={item.id}>
        <SearchResult searchResult={item} />
      </li>
    ))
    : null;

  const renderedResultsInfo = searchResults ? (
    <div className="search-page__results-filters">
      <SearchResultCount
        currentPage={parseInt(filters.page, 10)}
        pageSize={parseInt(filters.page_size, 10)}
        totalItems={parseInt(searchResults.total_items, 10)}
      />
      <div className="search-page__sort-filters">
        <span className="sort-content">Sort by:</span>
        <button
          className={`sort-date ${sortByDateClass ? 'active' : ''}`}
          type="button"
          value="Date"
          onClick={handleSortContent}
        >
          Latest
        </button>
        <button
          className={`sort-relevancy ${!sortByDateClass ? 'active' : ''}`}
          type="button"
          value="Relevancy"
          onClick={handleSortContent}
        >
          Relevancy
        </button>
      </div>
    </div>
  ) : null;

  // eslint-disable-next-line operator-linebreak
  const renderedPagination =
    searchResults && searchResults.total_items ? (
      <SearchPagination
        totalPages={parseInt(searchResults.total_pages, 10)}
        currentPage={parseInt(filters.page, 10)}
        onPageChanged={handlePageChange}
      />
    ) : null;

  if (error) return <h1>Error</h1>;

  if (warning) return (
    <div>
      {renderedTitle}
      <SearchExposedFilters
        newsSubtypes={newsSubtypes}
        podcastSubtypes={podcastSubtypes}
        newsConferences={newsConferences}
        specialties={specialties}
        opinionSubtypeSubLevel={opinionSubtypeSubLevel}
        filters={filters}
        setFilters={setFilters}
        search={search}
        updateSortModeClass={updateSortModeClass}
      />
      <div className="search-page__container">
        <div className="search-page__list">
          <h3>Please enter a keyword with at least 3 characters to find content on the site.</h3>
        </div>
        <div className="search-page__ads">
          <SearchAd type="short" id="div-gpt-ad-1477677870048-0" />
          <SearchAd type="tall" id="div-gpt-ad-1477677984444-0" />
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {renderedTitle}
      <SearchExposedFilters
        newsSubtypes={newsSubtypes}
        podcastSubtypes={podcastSubtypes}
        newsConferences={newsConferences}
        specialties={specialties}
        opinionSubtypeSubLevel={opinionSubtypeSubLevel}
        filters={filters}
        setFilters={setFilters}
        search={search}
        updateSortModeClass={updateSortModeClass}
      />
      <div className="search-page__container">
        <div className="search-page__list">
          {renderedResultsInfo}
          {renderedResultItems}
          {renderedPagination}
        </div>
        <div className="search-page__ads">
          <SearchAd type="short" id="div-gpt-ad-1477677870048-0" />
          <SearchAd type="tall" id="div-gpt-ad-1477677984444-0" />
        </div>
      </div>
    </div>
  );
}
